/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React from "react";
import { Button, Checkbox, FormControlLabel, Tooltip } from "@material-ui/core";
import PropTypes from "prop-types";
import { FormTextField } from "../../../../components";
import { useDashboardUrlConfigs } from "../../../dashboardManager/context/Provider";
import { DASHBOARDS_CONFIG_TOOLTIPS } from "../../../../utility/toolTipMessages";
import { checkDashboardURIErrors, extractSrcFromIframe, isValidIFrameCode } from "../../../../utility/helpers";

/**
 * This component renders a single dashboard url configuration card based on provided report name and URL.
 *
 * @param {Object} configured report details. (reportId, reportName, reportUrl, isHidden)
 */

const DashboardConfigCard = ({ dashboardUrlObj }) => {
	const { removeDashboardUrl, modifyDashboardUrlConfig } = useDashboardUrlConfigs();

	const updateDashboardUrlConfig = (valueName, value) => {
		modifyDashboardUrlConfig(dashboardUrlObj.reportId, valueName, value);
	};

	const hiddenCheckboxField = (
		<FormControlLabel
			style={{ display: "flex", alignItems: "center", marginTop: -4 }}
			control={
				<Checkbox
					checked={dashboardUrlObj.isHidden}
					color="secondary"
					size="small"
					onChange={(event) => updateDashboardUrlConfig("isHidden", event.target.checked)}
				/>
			}
			label={
				<span style={{ fontSize: 14, marginLeft: -6 }}>
					<span>Hidden</span>
					<Tooltip arrow interactive placement={"bottom-start"} title={DASHBOARDS_CONFIG_TOOLTIPS.HIDDEN}>
						<i style={{ height: "15px", marginLeft: "4px", color: "gray" }} className="fas fa-info-circle" />
					</Tooltip>
				</span>
			}
		/>
	);

	return (
		<div>
			<div className="d-flex">
				<div className="col-6 mt-1">
					<FormTextField
						label={"Dashboard Name"}
						formEdited
						startingValue={dashboardUrlObj.reportName}
						setConfigValue={updateDashboardUrlConfig}
						valueName="reportName"
						styleObject={{ flex: 1, width: "100%" }}
						enableDebounce
						errorCheck={() => dashboardUrlObj.errors.reportName}
						toolTipMessage={DASHBOARDS_CONFIG_TOOLTIPS.REPORT_NAME_FIELD}
					/>
				</div>
				<div className="d-flex align-items-start justify-content-end col-6">
					{hiddenCheckboxField}
					<Button
						size="small"
						variant="outlined"
						style={{ color: "#cc1f20" }}
						onClick={() => {
							removeDashboardUrl(dashboardUrlObj.reportId);
						}}
					>
						Remove
					</Button>
				</div>
			</div>
			<div className="d-flex mt-4 mb-2 col-12">
				<FormTextField
					label={"Dashboard URL / iFrame Embed Code"}
					formEdited
					multiline
					maxLineRows={3}
					startingValue={dashboardUrlObj.reportUrl}
					inputProps={{
						onFocus: (e) => e.target.select(),
						onPaste: (e) => {
							e.stopPropagation();
							e.preventDefault();
							const clipboardData = e.clipboardData || window.clipboardData;
							const pastedData = clipboardData.getData("Text");
							if (!checkDashboardURIErrors(pastedData)) {
								updateDashboardUrlConfig("reportUrl", pastedData);
							} else if (isValidIFrameCode(pastedData)) {
								const dashboardUrl = extractSrcFromIframe(pastedData);
								updateDashboardUrlConfig("reportUrl", dashboardUrl);
							} else {
								updateDashboardUrlConfig("reportUrl", pastedData);
							}
						},
					}}
					setConfigValue={updateDashboardUrlConfig}
					valueName="reportUrl"
					styleObject={{ minWidth: 120, flex: 1 }}
					enableDebounce
					errorCheck={() => dashboardUrlObj.errors.reportUrl}
					toolTipMessage={DASHBOARDS_CONFIG_TOOLTIPS.URL_FIELD}
				/>
			</div>
		</div>
	);
};

DashboardConfigCard.defaultProps = {
	dashboardUrlObj: {},
};
DashboardConfigCard.propTypes = {
	dashboardUrlObj: PropTypes.object,
};

export default DashboardConfigCard;
